/**
 * RBAC Hooks
 * Provides easy access to role-based permissions throughout the application
 */

import { useMemo } from 'react';
import { useOrganizationStore } from '../lib/stores/organization-store';
import { useUserStore } from '../lib/stores/user-store';
import {
  type UserRole,
  type PermissionLevel,
  type ResourcePermissions,
  type DataVisibilityConfig,
  type NavCategory,
  hasPermission,
  canViewNav,
  isNavReadonly,
  canViewData,
  isAdminRole,
  canManageBilling,
  canManageUsers,
  getRoleInfo,
  getResourcePermissions,
  DATA_VISIBILITY,
  NAV_VISIBILITY,
} from '../lib/config/rbac-permissions';

// Re-export types for convenience
export type { UserRole, PermissionLevel, ResourcePermissions, NavCategory };

// =============================================================================
// MAIN HOOK
// =============================================================================

export interface UseRBACReturn {
  // Current role
  role: UserRole;
  roleName: string;
  roleDescription: string;

  // Role checks
  isOwner: boolean;
  isAdmin: boolean;
  isAdminOrOwner: boolean;
  isDispatcher: boolean;
  isDriver: boolean;
  isAccountant: boolean;
  isSafety: boolean;
  isBroker: boolean;
  isFuelOperator: boolean;
  isMaintenanceOperator: boolean;
  isMaintenanceTechnician: boolean;

  // Permission checks
  can: (resource: string, action: string) => PermissionLevel;
  canView: (resource: string) => boolean;
  canCreate: (resource: string) => boolean;
  canUpdate: (resource: string) => boolean;
  canDelete: (resource: string) => boolean;
  canPerform: (resource: string, action: string) => boolean;
  isOwnOnly: (resource: string, action: string) => boolean;
  isViewOnly: (resource: string, action: string) => boolean;

  // Resource permissions
  getPermissions: (resource: string) => ResourcePermissions | null;

  // Navigation
  canViewNav: (category: NavCategory) => boolean;
  isNavReadonly: (category: NavCategory) => boolean;

  // Data visibility
  canViewRevenue: boolean;
  canViewCosts: boolean;
  canViewMargins: boolean;
  canViewSettlements: boolean;
  canViewCustomerRates: boolean;
  canViewDriverPay: boolean;
  canViewCompanyFinancials: boolean;
  canViewFinancialData: (dataType: keyof DataVisibilityConfig) => boolean;

  // Special permissions
  canManageBilling: boolean;
  canManageUsers: boolean;
}

/**
 * Main RBAC hook - provides all permission checking capabilities
 */
export function useRBAC(): UseRBACReturn {
  const { organization } = useOrganizationStore();
  const { user } = useUserStore();

  // Get role from organization first, fallback to user role, default to 'driver' (most restricted)
  const role = useMemo(() => {
    const orgRoles = organization?.roles as UserRole[] | undefined;
    const orgRole = orgRoles?.[0];
    const userRole = user?.role as UserRole | undefined;
    return orgRole || userRole || 'driver';
  }, [organization?.roles, user?.role]);

  return useMemo(() => {
    const info = getRoleInfo(role);
    const dataVis = DATA_VISIBILITY[role];

    return {
      // Current role
      role,
      roleName: info.name,
      roleDescription: info.description,

      // Role checks
      isOwner: role === 'owner',
      isAdmin: role === 'admin',
      isAdminOrOwner: isAdminRole(role),
      isDispatcher: role === 'dispatcher',
      isDriver: role === 'driver',
      isAccountant: role === 'accountant',
      isSafety: role === 'safety',
      isBroker: role === 'broker',
      isFuelOperator: role === 'fuel_operator',
      isMaintenanceOperator: role === 'maintenance_operator',
      isMaintenanceTechnician: role === 'maintenance_technician',

      // Permission checks
      can: (resource: string, action: string) => hasPermission(role, resource, action),

      canView: (resource: string) => {
        const perm = hasPermission(role, resource, 'view');
        return perm === true || perm === 'view' || perm === 'own';
      },

      canCreate: (resource: string) => {
        const perm = hasPermission(role, resource, 'create');
        return perm === true || perm === 'own';
      },

      canUpdate: (resource: string) => {
        const perm = hasPermission(role, resource, 'update');
        return perm === true || perm === 'own';
      },

      canDelete: (resource: string) => {
        const perm = hasPermission(role, resource, 'delete');
        return perm === true;
      },

      canPerform: (resource: string, action: string) => {
        const perm = hasPermission(role, resource, action);
        return perm === true || perm === 'own';
      },

      isOwnOnly: (resource: string, action: string) => {
        return hasPermission(role, resource, action) === 'own';
      },

      isViewOnly: (resource: string, action: string) => {
        return hasPermission(role, resource, action) === 'view';
      },

      // Resource permissions
      getPermissions: (resource: string) => getResourcePermissions(role, resource),

      // Navigation
      canViewNav: (category: NavCategory) => canViewNav(role, category),
      isNavReadonly: (category: NavCategory) => isNavReadonly(role, category),

      // Data visibility
      canViewRevenue: dataVis?.revenue ?? false,
      canViewCosts: dataVis?.costs ?? false,
      canViewMargins: dataVis?.margins ?? false,
      canViewSettlements: dataVis?.settlements ?? false,
      canViewCustomerRates: dataVis?.customerRates ?? false,
      canViewDriverPay: dataVis?.driverPay ?? false,
      canViewCompanyFinancials: dataVis?.companyFinancials ?? false,
      canViewFinancialData: (dataType: keyof DataVisibilityConfig) => canViewData(role, dataType),

      // Special permissions
      canManageBilling: canManageBilling(role),
      canManageUsers: canManageUsers(role),
    };
  }, [role]);
}

// =============================================================================
// RESOURCE-SPECIFIC HOOKS
// =============================================================================

/**
 * Hook for checking permissions on a specific resource
 */
export function useResourcePermissions(resource: string) {
  const { role } = useRBAC();

  return useMemo(() => {
    const permissions = getResourcePermissions(role, resource);

    if (!permissions) {
      return {
        canView: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        isViewOnly: false,
        isOwnOnly: false,
        permissions: null,
      };
    }

    const viewPerm = permissions.view;
    const createPerm = permissions.create;
    const updatePerm = permissions.update;
    const deletePerm = permissions.delete;

    return {
      canView: viewPerm === true || viewPerm === 'view' || viewPerm === 'own',
      canCreate: createPerm === true || createPerm === 'own',
      canUpdate: updatePerm === true || updatePerm === 'own',
      canChangeStatus: true,
      canDelete: deletePerm === true,
      isViewOnly: viewPerm === 'view',
      isOwnOnly: viewPerm === 'own' || updatePerm === 'own',
      permissions,
    };
  }, [role, resource]);
}

/**
 * Hook for load permissions
 */
export function useLoadPermissions() {
  const perms = useResourcePermissions('loads');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canClone: hasPermission(role, 'loads', 'clone') === true,
    canAssign: hasPermission(role, 'loads', 'assign') === true,
    canUpdateStatus: (() => {
      const p = hasPermission(role, 'loads', 'statusUpdate');
      return p === true || p === 'own';
    })(),
    isStatusUpdateOwnOnly: hasPermission(role, 'loads', 'statusUpdate') === 'own',
  }), [perms, role]);
}

/**
 * Hook for driver permissions
 */
export function useDriverPermissions() {
  const perms = useResourcePermissions('drivers');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canViewLocation: (() => {
      const p = hasPermission(role, 'drivers', 'viewLocation');
      return p === true || p === 'own';
    })(),
    canViewHOS: (() => {
      const p = hasPermission(role, 'drivers', 'viewHOS');
      return p === true || p === 'view' || p === 'own';
    })(),
    isLocationOwnOnly: hasPermission(role, 'drivers', 'viewLocation') === 'own',
    isHOSOwnOnly: hasPermission(role, 'drivers', 'viewHOS') === 'own',
  }), [perms, role]);
}

/**
 * Hook for invoice permissions
 */
export function useInvoicePermissions() {
  const perms = useResourcePermissions('invoices');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canSend: hasPermission(role, 'invoices', 'send') === true,
    canGenerateBatch: hasPermission(role, 'invoices', 'generateBatch') === true,
    canRecordPayment: hasPermission(role, 'invoices', 'recordPayment') === true,
  }), [perms, role]);
}

/**
 * Hook for settlement permissions
 */
export function useSettlementPermissions() {
  const perms = useResourcePermissions('settlements');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canApprove: hasPermission(role, 'settlements', 'approve') === true,
    canPay: hasPermission(role, 'settlements', 'pay') === true,
  }), [perms, role]);
}

/**
 * Hook for document permissions
 */
export function useDocumentPermissions() {
  const perms = useResourcePermissions('documents');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canGenerate: (() => {
      const p = hasPermission(role, 'documents', 'generate');
      return p === true;
    })(),
    canSend: (() => {
      const p = hasPermission(role, 'documents', 'send');
      return p === true;
    })(),
  }), [perms, role]);
}

/**
 * Hook for maintenance permissions
 */
export function useMaintenancePermissions() {
  const perms = useResourcePermissions('maintenance');
  return useMemo(() => ({ ...perms }), [perms]);
}

/**
 * Hook for work order permissions
 */
export function useWorkOrderPermissions() {
  const perms = useResourcePermissions('work_orders');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canAssign: hasPermission(role, 'work_orders', 'assign') === true,
    canUpdateStatus: hasPermission(role, 'work_orders', 'statusUpdate') === true,
  }), [perms, role]);
}

/**
 * Hook for parts permissions
 */
export function usePartsPermissions() {
  return useResourcePermissions('parts');
}

/**
 * Hook for paystub permissions
 */
export function usePaystubPermissions() {
  const perms = useResourcePermissions('paystubs');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canApprove: hasPermission(role, 'paystubs', 'approve') === true,
    canProcess: hasPermission(role, 'paystubs', 'process') === true,
  }), [perms, role]);
}

/**
 * Hook for dispatch permissions
 */
export function useDispatchPermissions() {
  const perms = useResourcePermissions('dispatch');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canAssign: hasPermission(role, 'dispatch', 'assign') === true,
    canUpdateStatus: hasPermission(role, 'dispatch', 'statusUpdate') === true,
  }), [perms, role]);
}

/**
 * Hook for member permissions
 */
export function useMemberPermissions() {
  const perms = useResourcePermissions('members');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canInvite: hasPermission(role, 'members', 'invite') === true,
  }), [perms, role]);
}

/**
 * Hook for inspection permissions
 */
export function useInspectionPermissions() {
  const perms = useResourcePermissions('inspections');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canResolveDefects: hasPermission(role, 'inspections', 'resolveDefects') === true,
  }), [perms, role]);
}

/**
 * Hook for quote permissions
 */
export function useQuotePermissions() {
  const perms = useResourcePermissions('quotes');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canSend: hasPermission(role, 'quotes', 'send') === true,
    canConvert: hasPermission(role, 'quotes', 'convert') === true,
  }), [perms, role]);
}

/**
 * Hook for recurring load permissions
 */
export function useRecurringPermissions() {
  const perms = useResourcePermissions('recurring');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canGenerate: hasPermission(role, 'recurring', 'generate') === true,
    canPauseResume: hasPermission(role, 'recurring', 'pauseResume') === true,
  }), [perms, role]);
}

/**
 * Hook for carrier permissions
 */
export function useCarrierPermissions() {
  const perms = useResourcePermissions('carriers');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canVerify: hasPermission(role, 'carriers', 'verify') === true,
    canTender: hasPermission(role, 'carriers', 'tender') === true,
    canViewPerformance: (() => {
      const p = hasPermission(role, 'carriers', 'viewPerformance');
      return p === true || p === 'view';
    })(),
  }), [perms, role]);
}

/**
 * Hook for settings permissions
 */
export function useSettingsPermissions() {
  const perms = useResourcePermissions('settings');
  const { role } = useRBAC();

  return useMemo(() => ({
    ...perms,
    canManageUsers: hasPermission(role, 'settings', 'manageUsers') === true,
    canManageBilling: hasPermission(role, 'settings', 'manageBilling') === true,
    canManageIntegrations: hasPermission(role, 'settings', 'manageIntegrations') === true,
    canViewAuditLogs: hasPermission(role, 'settings', 'viewAuditLogs') === true,
    canManageApiKeys: hasPermission(role, 'settings', 'manageApiKeys') === true,
  }), [perms, role]);
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to check if user can view a specific navigation item
 */
export function useCanViewNav(category: NavCategory): boolean {
  const { canViewNav } = useRBAC();
  return canViewNav(category);
}

/**
 * Hook to check if a navigation item is readonly
 */
export function useIsNavReadonly(category: NavCategory): boolean {
  const { isNavReadonly } = useRBAC();
  return isNavReadonly(category);
}

/**
 * Hook to check if user can view financial data
 */
export function useCanViewFinancials() {
  const rbac = useRBAC();

  return useMemo(() => ({
    canViewRevenue: rbac.canViewRevenue,
    canViewCosts: rbac.canViewCosts,
    canViewMargins: rbac.canViewMargins,
    canViewSettlements: rbac.canViewSettlements,
    canViewCustomerRates: rbac.canViewCustomerRates,
    canViewDriverPay: rbac.canViewDriverPay,
    canViewCompanyFinancials: rbac.canViewCompanyFinancials,
    canViewAny: rbac.canViewRevenue || rbac.canViewCosts || rbac.canViewMargins || rbac.canViewCompanyFinancials,
  }), [rbac]);
}

/**
 * Hook to get all visible navigation categories
 */
export function useVisibleNavCategories(): NavCategory[] {
  const { role } = useRBAC();

  return useMemo(() => {
    const navConfig = NAV_VISIBILITY[role] || {};
    return Object.entries(navConfig)
      .filter(([, config]) => config?.visible)
      .map(([category]) => category as NavCategory);
  }, [role]);
}

/**
 * Hook to check a specific permission (simple boolean check)
 */
export function useHasPermission(resource: string, action: string): boolean {
  const { canPerform } = useRBAC();
  return canPerform(resource, action);
}

/**
 * Hook to check multiple permissions at once
 */
export function useHasAnyPermission(checks: Array<{ resource: string; action: string }>): boolean {
  const { can } = useRBAC();

  return useMemo(() => {
    return checks.some(({ resource, action }) => {
      const perm = can(resource, action);
      return perm === true || perm === 'own';
    });
  }, [can, checks]);
}

/**
 * Hook to check if all permissions are granted
 */
export function useHasAllPermissions(checks: Array<{ resource: string; action: string }>): boolean {
  const { can } = useRBAC();

  return useMemo(() => {
    return checks.every(({ resource, action }) => {
      const perm = can(resource, action);
      return perm === true || perm === 'own';
    });
  }, [can, checks]);
}
